import type {
  QuestionAnswer,
  RecordResponseRequest,
  SectionViewQuestion,
  SubSection,
} from '@assessments/shared'
import { isAnswered } from '../response-autosave/validation.js'

export interface ProgressCounts {
  answered: number
  total: number
}

export interface SectionProgressCounts {
  bySubSection: Map<string, ProgressCounts>
  section: ProgressCounts
}

/**
 * Whether a loaded answer counts as answered. Mirrors the API's `isAnswered` in
 * apps/api/src/services/section-view.service.ts; the two must change together.
 */
export function baselineAnswered(answer: QuestionAnswer | null): boolean {
  return (
    answer !== null && answer.value !== null && !answer.isSkipped && answer.source === 'respondent'
  )
}

/** A confirmed save since load wins over the loaded answer. */
export function isQuestionAnswered(
  question: SectionViewQuestion,
  confirmed: Record<string, RecordResponseRequest>,
): boolean {
  const saved = confirmed[question.id]
  return saved ? isAnswered(saved) : baselineAnswered(question.answer)
}

/** Answered/total per presented sub-section, and their sum for the section. Questions
 * skip logic hides count toward neither. */
export function deriveSectionProgress(
  subSections: readonly SubSection[],
  confirmed: Record<string, RecordResponseRequest>,
  hidden: ReadonlySet<string> = new Set(),
): SectionProgressCounts {
  const bySubSection = new Map<string, ProgressCounts>()
  const section: ProgressCounts = { answered: 0, total: 0 }
  for (const sub of subSections) {
    const visible = sub.questions.filter((q) => !hidden.has(q.id))
    const counts = {
      answered: visible.filter((q) => isQuestionAnswered(q, confirmed)).length,
      total: visible.length,
    }
    bySubSection.set(sub.id, counts)
    section.answered += counts.answered
    section.total += counts.total
  }
  return { bySubSection, section }
}
